import React, { useCallback, useContext, useRef, useState } from 'react';
import { useSpring } from '@react-spring/web';
import { PageContentStateContext } from '@src/contexts/wysiwyg/page_content';
import type {
  TextCacheItem,
  TTextBoxContentItem,
  TTextContentItem
} from '@src/types/wysiwyg_editor';
import { isTextBox } from '@src/utils/wysiwyg_editor/data';
import { useDispatch } from 'react-redux';
import {
  addPageContentItem,
  saveSnapshot,
  textCacheAddItem,
  textCacheRemoveItem
} from '@src/actions/wysiwyg/editor';
import {
  TextCacheDispatchContext,
  TextCacheStateContext
} from '@src/contexts/wysiwyg/text_cache';
import useSetTextBoxContent from '@src/hooks/wysiwyg_editor/useSetTextBoxContent';
import { PositionableTextDocument } from '@src/lib/PositionableTextDocument';
import TextEditor from './TextEditor';
import { EditorView } from 'prosemirror-view';
import { stylesToDefaultMarkValues } from '@src/utils/wysiwyg_editor/styles';
import { TRect } from '@src/types/misc';
import { useTextBoxGenerator } from '@src/hooks/wysiwyg_editor/useTextBoxGenerator';
import useConstrainedPageItemLocation from '@src/hooks/wysiwyg_editor/useConstrainedPageItemLocation';

interface MeasureContext {
  source: TTextContentItem;
  target: TTextContentItem;
}

function TextDropOverlay(): React.ReactElement {
  const dispatch = useDispatch();
  const textBoxGenerator = useTextBoxGenerator();
  const overlayRef = useRef<HTMLDivElement>();
  const { contentItems } = useContext(PageContentStateContext);
  const { dragging } = useContext(TextCacheStateContext);
  const [contextToMeasure, setContextToMeasure] = useState<MeasureContext>(
    null
  );
  const setTextBoxContent = useSetTextBoxContent();
  const constrainedPageItemLocation = useConstrainedPageItemLocation();
  const { stopDragging } = useContext(TextCacheDispatchContext);

  const [springValues, springApi] = useSpring<TRect>(() => ({
    config: { duration: 1 },
    ...{ height: 0, width: 0, x: 0, y: 0 }
  }));

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    []
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      (event.target as HTMLDivElement).classList.add(
        'wysiwyg-editor-drag-target--active'
      );
    },
    []
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      (event.target as HTMLDivElement).classList.remove(
        'wysiwyg-editor-drag-target--active'
      );
    },
    []
  );

  const handleTargetDrop = useCallback(
    (event: React.DragEvent<HTMLElement>, pageItem: TTextBoxContentItem) => {
      event.preventDefault();
      event.stopPropagation();

      const { data } = JSON.parse(event.dataTransfer.getData('Text')) as {
        data: TextCacheItem;
      };

      const overlayRect = overlayRef.current.getBoundingClientRect();

      springApi.set({
        height: pageItem.containerLocation.height * overlayRect.height,
        width: pageItem.containerLocation.width * overlayRect.width,
        x: pageItem.containerLocation.x * overlayRect.x,
        y: pageItem.containerLocation.y * overlayRect.y
      });

      dispatch(textCacheAddItem(pageItem));
      dispatch(saveSnapshot());
      dispatch(textCacheRemoveItem(data.uuid));
      setContextToMeasure({
        source: data.item,
        target: pageItem
      });
    },
    [dispatch, springApi]
  );

  const handleOverlayDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const { data } = JSON.parse(event.dataTransfer.getData('Text')) as {
        data: TextCacheItem;
        dragOffset: { x: number; y: number };
      };

      const overlayPosition = (event.target as HTMLDivElement).getBoundingClientRect();
      const dropX = event.clientX - overlayPosition.left;
      const dropY = event.clientY - overlayPosition.top;

      const location = constrainedPageItemLocation({
        dimensions: data.item.containerLocation,
        hopefulLocation: { x: dropX, y: dropY },
        origin: { x: 0, y: 0 },
        dimensionsMeasurementType: '%',
        locationMeasurementType: 'px',
        returnMeasurementType: '%'
      });

      const newTextItem = textBoxGenerator({
        containerLocation: {
          ...data.item.containerLocation,
          ...location
        },
        contentLocation: data.item.contentLocation
      });

      const overlayRect = overlayRef.current.getBoundingClientRect();

      springApi.set({
        height: newTextItem.containerLocation.height * overlayRect.height,
        width: newTextItem.containerLocation.width * overlayRect.width,
        x: newTextItem.containerLocation.x * overlayRect.x,
        y: newTextItem.containerLocation.y * overlayRect.y
      });

      dispatch(saveSnapshot());
      dispatch(addPageContentItem(newTextItem));
      dispatch(textCacheRemoveItem(data.uuid));
      setContextToMeasure({
        source: data.item,
        target: newTextItem
      });
    },
    [constrainedPageItemLocation, dispatch, springApi, textBoxGenerator]
  );

  if (!dragging && !contextToMeasure) return null;

  return (
    <div
      style={{
        // image/text elements including the content and their drag handles use
        // indexes in the hundreds, so do our best to be higher
        zIndex: 1000
      }}
      className="wysiwyg-editor-drag-target--overlay"
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleOverlayDrop}
      ref={overlayRef}
    >
      {dragging
        ? contentItems.map((item) => {
            if (!isTextBox(item)) return null;

            return (
              <div
                style={{
                  position: 'absolute',
                  left: `${item.containerLocation.x * 100}%`,
                  top: `${item.containerLocation.y * 100}%`,
                  height: `${item.containerLocation.height * 100}%`,
                  width: `${item.containerLocation.width * 100}%`
                }}
                key={item.id}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleTargetDrop(e, item)}
              />
            );
          })
        : null}

      {contextToMeasure && contextToMeasure.source.content ? (
        <div style={{ visibility: 'hidden' }}>
          <TextEditor
            defaultAlignment="left"
            initialContent={PositionableTextDocument.fromPositionedDocument(
              contextToMeasure.source.content
            )}
            location={springValues}
            textItem={contextToMeasure.target}
            localDefaultMarkValues={stylesToDefaultMarkValues(
              {},
              contextToMeasure.source.styles
            )}
            isSingleLine={false}
            onViewMount={(view: EditorView) => {
              const positionedText = PositionableTextDocument.fromProseMirrorEditorView(
                view
              ).toPositionedDocument();

              setTextBoxContent(contextToMeasure.target, positionedText, false);
              setContextToMeasure(null);

              // since we are removing the dragged element, the ondragend
              // handler doesn't always fire so calling stopDragging there
              // doesn't work well
              stopDragging();
            }}
            textOverflows={false}
            overflowIndicatorPosition={{}}
            disableAutosave
          />
        </div>
      ) : null}
    </div>
  );
}

export default TextDropOverlay;
